'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'
import { ScrollRow } from '@/components/ui/scroll-row'

import {
  EASE,
  HAIRLINE,
  PANEL,
  PANEL_ACTIVE,
  PANEL_RESTING,
  SectionHead,
  useReveal,
} from './shared'

/* Role-based access control, demonstrated instead of claimed. One student
   record; changing the role does not swap the pane for a different pane — the
   forbidden fields collapse to a redaction bar in place, so you watch the
   permission model operate on the same row.

   The role list follows the homepage solutions nav: same ScrollRow behaviour
   on phones, same "a tab never takes the accent as a fill" rule — the active
   state is a denser panel plus a masked accent hairline on the leading edge. */

const ROLES = [
  { id: 'registrar', name: 'Registrar', scope: 'Institution-wide' },
  { id: 'hod', name: 'Head of Department', scope: 'Own department' },
  { id: 'faculty', name: 'Faculty mentor', scope: 'Assigned mentees' },
  { id: 'parent', name: 'Parent', scope: 'Own ward' },
] as const

type RoleId = (typeof ROLES)[number]['id']

const FIELDS: {
  label: string
  value: string
  /** Redaction bar width — varied so the pane never reads as a loading skeleton. */
  bar: string
  seenBy: RoleId[]
}[] = [
  {
    label: 'Student',
    value: 'Aarav Deshmukh · 21CSE1184',
    bar: '58%',
    seenBy: ['registrar', 'hod', 'faculty', 'parent'],
  },
  {
    label: 'Programme',
    value: 'B.Tech Computer Science · Semester 5',
    bar: '72%',
    seenBy: ['registrar', 'hod', 'faculty', 'parent'],
  },
  {
    label: 'Attendance, current semester',
    value: '91.2% · 412 of 452 sessions',
    bar: '46%',
    seenBy: ['registrar', 'hod', 'faculty', 'parent'],
  },
  {
    label: 'Internal assessment',
    value: 'CIE 1: 27/30 · CIE 2: 25/30',
    bar: '52%',
    seenBy: ['registrar', 'hod', 'faculty', 'parent'],
  },
  {
    label: 'Fee ledger',
    value: '₹0 outstanding · last receipt #24118',
    bar: '64%',
    seenBy: ['registrar', 'parent'],
  },
  {
    label: 'Scholarship & category',
    value: 'EWS · state scholarship sanctioned',
    bar: '50%',
    seenBy: ['registrar'],
  },
  {
    label: 'Counselling & wellbeing',
    value: '2 sessions logged · mentor Dr. R. Kulkarni',
    bar: '68%',
    seenBy: ['registrar', 'faculty'],
  },
  {
    label: 'Disciplinary record',
    value: 'No entries',
    bar: '32%',
    seenBy: ['registrar', 'hod'],
  },
]

export function UdcAccess() {
  const [role, setRole] = React.useState<RoleId>('faculty')
  const section = useReveal<HTMLDivElement>(0.1)
  const tabRefs = React.useRef<(HTMLButtonElement | null)[]>([])

  const activeIndex = ROLES.findIndex((r) => r.id === role)
  const visibleCount = FIELDS.filter((f) => f.seenBy.includes(role)).length

  const onKeyDown = (e: React.KeyboardEvent) => {
    const last = ROLES.length - 1
    let next = -1
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight')
      next = activeIndex === last ? 0 : activeIndex + 1
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft')
      next = activeIndex === 0 ? last : activeIndex - 1
    if (e.key === 'Home') next = 0
    if (e.key === 'End') next = last
    if (next < 0) return
    e.preventDefault()
    setRole(ROLES[next]!.id)
    tabRefs.current[next]?.focus()
  }

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <SectionHead
          eyebrow="Access model"
          title="Everyone opens the same record. Nobody sees the same fields."
          deck="One student, one row, one datastore. What changes with the role is not the data: it is what the platform will render, and every read is written to the audit trail."
        />

        <div
          ref={section.ref}
          className="mt-10 grid grid-cols-1 items-stretch gap-4 sm:mt-12 lg:grid-cols-[280px_1fr] lg:gap-6"
          style={{
            opacity: section.shown ? 1 : 0,
            transform: section.shown ? 'none' : 'translateY(16px)',
            transition: `opacity 700ms ${EASE}, transform 700ms ${EASE}`,
          }}
        >
          {/* ── Role selector ── */}
          <ScrollRow
            role="tablist"
            aria-label="Viewing role"
            aria-orientation="vertical"
            onKeyDown={onKeyDown}
            wrapperClassName="lg:h-full"
            className="flex flex-row gap-2.5 pb-1 lg:h-full lg:flex-col lg:overflow-visible lg:pb-0"
          >
            {ROLES.map((r, i) => {
              const on = r.id === role
              return (
                <button
                  key={r.id}
                  ref={(el) => {
                    tabRefs.current[i] = el
                  }}
                  role="tab"
                  type="button"
                  id={`udc-role-${r.id}`}
                  aria-selected={on}
                  aria-controls="udc-record"
                  tabIndex={on ? 0 : -1}
                  onClick={() => setRole(r.id)}
                  className={cn(
                    'group relative min-w-[168px] shrink-0 overflow-hidden rounded-2xl px-4 py-3.5 text-left outline-none lg:min-w-0 lg:flex-1',
                    'transition-colors [transition-duration:250ms]',
                    'focus-visible:ring-2 focus-visible:ring-emerald-600/50 dark:focus-visible:ring-primary-active/50',
                    on ? PANEL_ACTIVE : PANEL_RESTING
                  )}
                  style={{ transitionTimingFunction: EASE }}
                >
                  {/* Active marker: a masked hairline down the leading edge,
                      not a filled state. */}
                  <span
                    aria-hidden
                    className={cn(
                      'absolute inset-y-2 left-0 w-[2px] rounded-full transition-opacity [transition-duration:250ms]',
                      'bg-emerald-600 dark:bg-primary-active',
                      on ? 'opacity-100' : 'opacity-0'
                    )}
                    style={{
                      WebkitMaskImage:
                        'linear-gradient(to bottom, transparent, #000 22%, #000 78%, transparent)',
                      maskImage:
                        'linear-gradient(to bottom, transparent, #000 22%, #000 78%, transparent)',
                    }}
                  />

                  <div
                    className={cn(
                      'font-heading text-[13.5px] font-normal leading-tight transition-colors [transition-duration:250ms]',
                      on ? 'text-foreground' : 'text-foreground/70'
                    )}
                  >
                    {r.name}
                  </div>
                  <div
                    className={cn(
                      'mt-1 text-[11px] font-medium transition-colors [transition-duration:250ms]',
                      on ? 'text-muted-foreground' : 'text-muted-foreground/60'
                    )}
                  >
                    {r.scope}
                  </div>
                </button>
              )
            })}
          </ScrollRow>

          {/* ── The record ── */}
          <div
            id="udc-record"
            role="tabpanel"
            aria-labelledby={`udc-role-${role}`}
            className={cn('overflow-hidden rounded-2xl sm:rounded-3xl', PANEL)}
          >
            <div
              className={cn(
                'flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b px-5 py-4 sm:px-7',
                HAIRLINE
              )}
            >
              <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground sm:text-[11px]">
                Student record · 21CSE1184
              </span>
              <span className="text-[11px] tabular-nums text-muted-foreground sm:text-xs">
                <span className="text-foreground">{visibleCount}</span> of{' '}
                {FIELDS.length} fields released to this role
              </span>
            </div>

            <ul className="px-5 sm:px-7">
              {FIELDS.map((f, i) => {
                const visible = f.seenBy.includes(role)
                return (
                  <li
                    key={f.label}
                    className={cn(
                      'grid grid-cols-1 gap-1 border-b py-3.5 last:border-0 sm:grid-cols-[minmax(0,200px)_1fr] sm:items-center sm:gap-6',
                      HAIRLINE
                    )}
                    style={{
                      opacity: section.shown ? 1 : 0,
                      transition: `opacity 380ms ${EASE} ${i * 55}ms`,
                    }}
                  >
                    <span
                      className={cn(
                        'text-[11px] transition-colors duration-300 sm:text-xs',
                        visible
                          ? 'text-muted-foreground'
                          : 'text-muted-foreground/50'
                      )}
                    >
                      {f.label}
                    </span>

                    {/* Value and redaction share one grid cell, so the row
                        height never changes as the role does. */}
                    <span className="grid min-h-[20px] items-center">
                      <span
                        className={cn(
                          '[grid-area:1/1] text-[12.5px] leading-snug text-foreground transition-opacity duration-300 sm:text-[13.5px]',
                          visible ? 'opacity-100' : 'opacity-0'
                        )}
                        style={{ transitionTimingFunction: EASE }}
                        aria-hidden={!visible}
                      >
                        {f.value}
                      </span>

                      <span
                        aria-hidden={visible}
                        className="[grid-area:1/1] flex items-center gap-2.5"
                      >
                        <span
                          className="h-2 rounded-full bg-foreground/10 transition-[width,opacity] [transition-duration:420ms]"
                          style={{
                            width: visible ? '0%' : f.bar,
                            opacity: visible ? 0 : 1,
                            transitionTimingFunction: EASE,
                          }}
                        />
                        <span
                          className="shrink-0 text-[10px] uppercase tracking-[0.14em] text-muted-foreground/45 transition-opacity duration-300"
                          style={{ opacity: visible ? 0 : 1 }}
                        >
                          {visible ? '' : 'Restricted'}
                        </span>
                      </span>
                    </span>
                  </li>
                )
              })}
            </ul>

            <div className={cn('border-t px-5 py-4 sm:px-7', HAIRLINE)}>
              <p className="text-[11px] leading-relaxed text-muted-foreground sm:text-xs">
                Redaction happens at the query, not in the interface: a
                restricted field never leaves the server. Granting one is an
                audit event, not a settings toggle.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
